<script setup lang="ts">
import { computed } from 'vue';
import MapContainer from '../map/MapContainer.vue';
import MarkerInfo from '@/components/map/MarkerInfo.vue';
import { Locales } from '@/store/language.interface';
import type { MapPoint, MapPointPlacement } from '@/components/map/map.types';
import { getLabel } from '@/utils/data-display-utils';
import { getMapMaxMarkers } from '@/utils/map-marker-cap';

// PROPS
const props = defineProps<{
  id: string;
  mapConfig: any;
  mapMarkers: any[];
  /** The participant's markers on this map, as its cap counts them. */
  participantMarkerCount?: number;
  showQuestionResults: boolean;
  languageEnabled?: Locales[];
}>();

// EMITS
const emit = defineEmits<{
  (e: 'submitMarker', marker);
  (e: 'likeMarker', properties: Record<string, any>): void;
  (e: 'verifyUser'): void;
  (e: 'mapStart'): void;
}>();

// COMPUTED
const maxMarkers = computed(() => getMapMaxMarkers(props.mapConfig));

const markerLimitReached = computed(() => (props.participantMarkerCount ?? 0) >= maxMarkers.value);

const points = computed<MapPoint[]>(() =>
  (props.mapMarkers ?? [])
    // A marker without usable coordinates cannot be drawn, and passing it on would break the
    // latitude/longitude contract the map component relies on.
    .filter(
      (marker) =>
        Number.isFinite(Number(marker.marker_location?.[0])) && Number.isFinite(Number(marker.marker_location?.[1])),
    )
    .map((marker, index) => ({
      // clientKey identifies a marker the participant just placed, which has no comment_id yet.
      // The index is only a last resort; it shifts whenever the list changes.
      id: marker.comment_id ?? marker.clientKey ?? `marker-${index}`,
      longitude: Number(marker.marker_location[0]),
      latitude: Number(marker.marker_location[1]),
      category: marker.marker_category,
      label: marker.marker_text,
      payload: {
        markerUserId: marker.user_id,
        markerUser: marker.user,
        markerLike: marker.likes,
        markerCommentId: marker.comment_id,
        markerFormId: marker.form_id,
      },
    })),
);

// FUNCTIONS
function pointPlaced(point: MapPointPlacement) {
  emit('submitMarker', {
    marker_location: [point.longitude, point.latitude],
    marker_category: point.category,
    marker_text: point.label,
    marker_approved: {},
  });
}

function pointAction(properties: Record<string, any>) {
  emit('likeMarker', properties);
}

function verifyUser() {
  emit('verifyUser');
}

function mapStart() {
  emit('mapStart');
}
</script>
<template>
  <div class="map-question">
    <!-- The map takes no point while it shows results (Map.vue), so the cap is only told when one can be added. -->
    <p v-if="!props.showQuestionResults" class="map-question__max-markers" aria-live="polite">
      <q-icon name="info_outline" />
      <template v-if="markerLimitReached">
        {{ getLabel('form.map.max-markers-reached', props.languageEnabled, { named: { max: maxMarkers } }) }}
      </template>
      <template v-else>
        {{
          getLabel('form.map.max-markers', props.languageEnabled, { named: { max: maxMarkers }, plural: maxMarkers })
        }}
      </template>
    </p>
    <MapContainer
      :id="props.id"
      :mapConfig="props.mapConfig"
      :points="points"
      :show-question-results="showQuestionResults"
      :allow-point-placement="true"
      :point-placement-disabled="markerLimitReached"
      :language-enabled="props.languageEnabled"
      @point-placed="pointPlaced"
      @point-action="pointAction"
      @verify-user="verifyUser"
      @map-start="mapStart"
    >
      <template #marker-content="{ marker, category, action }">
        <MarkerInfo :marker="marker" :category="category" @like-action="action" />
      </template>
    </MapContainer>
  </div>
</template>
<style lang="scss">
.map-question {
  background-color: $color-neutral-grey-300;
  padding: 12px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;

  &__max-markers {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 8px;
  }

  .map {
    width: 100%;

    .mapboxgl-ctrl-group:not(:empty) {
      box-shadow: rgba(100, 100, 111, 0.2) 0px 7px 29px 0px;

      button {
        color: $color-primary-darken;
      }
    }
  }
}
</style>
